/**
 * A conversation's messages as a bounded, gap-free window.
 *
 * Opening a conversation used to subscribe to `conversations/{id}/messages` — the WHOLE
 * history, live, re-delivered in full to every open window. Now only the newest PAGE_SIZE
 * messages are live; older ones are fetched a page at a time on request.
 *
 * ORDERING is by push key, not by `timestamp`. Push keys are unique and chronological, so:
 *   - pages cannot overlap or skip (the cursor is an exact key, never a value two
 *     messages can share — two messages in the same millisecond would break a timestamp
 *     cursor);
 *   - a message whose serverTimestamp is still pending locally does not jump around.
 *
 * Everything here is pure; `useMessageWindow` does the Firebase calls.
 */

export const PAGE_SIZE = 40;
/** Hard ceiling when paging back to reach one message (a pinned or searched-for one). */
export const MAX_SEEK_PAGES = 25;
export const SEEK_PAGE_SIZE = 200;

const byKey = (a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

/** RTDB snapshot value → message list. Non-object entries are ignored. */
export function entriesOf(val) {
    if (!val || typeof val !== 'object') return [];
    return Object.entries(val)
        .filter(([, m]) => m && typeof m === 'object')
        .map(([id, m]) => ({ ...m, id }));
}

/**
 * Apply the LIVE window (the newest `pageSize` messages) to what we hold.
 *
 * The window slides: when a new message arrives, the oldest one leaves the window. That
 * message still exists, so it must NOT be dropped from the screen. But a message that
 * is missing from inside the window's range genuinely no longer exists (hard-deleted)
 * and should go. And a window holding fewer than `pageSize` messages IS the whole
 * conversation, so it replaces everything.
 *
 * @param {Map<string, object>} held
 * @param {object[]} windowMsgs
 * @param {number} pageSize
 * @returns {{held: Map<string, object>, complete: boolean}} complete = nothing older exists
 */
export function applyLiveWindow(held, windowMsgs, pageSize = PAGE_SIZE) {
    const next = new Map();
    if (windowMsgs.length < pageSize) {
        for (const m of windowMsgs) next.set(m.id, m);
        return { held: next, complete: true };
    }
    const floor = windowMsgs.reduce((lo, m) => (lo === null || m.id < lo ? m.id : lo), null);
    for (const [id, m] of held) if (id < floor) next.set(id, m);     // older, outside the window: keep
    for (const m of windowMsgs) next.set(m.id, m);                    // the window is authoritative in its range
    return { held: next, complete: false };
}

/**
 * Merge an OLDER page. Returns whether anything older still exists.
 * A page shorter than requested means the start of the conversation was reached.
 */
export function applyOlderPage(held, pageMsgs, requested) {
    const next = new Map(held);
    for (const m of pageMsgs) if (!next.has(m.id)) next.set(m.id, m);   // live data wins over a stale page
    return { held: next, complete: pageMsgs.length < requested };
}

/** The display list: chronological by push key. */
export const toList = (held) => [...held.values()].sort(byKey);

/** The cursor for the next older page — the oldest key held. */
export function oldestKey(held) {
    let lo = null;
    for (const id of held.keys()) if (lo === null || id < lo) lo = id;
    return lo;
}

/** The newest key held — used to decide whether to auto-scroll. */
export function newestKey(held) {
    let hi = null;
    for (const id of held.keys()) if (hi === null || id > hi) hi = id;
    return hi;
}
